/** @jsxImportSource @emotion/react */
import { css, keyframes } from "@emotion/react"
import { withMyTheme } from "../theme/theme"
import { mobileCss } from "../theme/isMobile"

const float = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-12px); }
`

const pulse = keyframes`
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.15); }
`

const shimmer = keyframes`
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
`

const ContainerStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3vh;
    padding: 6vh 4vw;

    ${mobileCss(`
        gap: 2.5vh;
        padding: 4vh 6vw;
    `)}
`)

const EmojiRowStyle = withMyTheme(() => css`
    display: flex;
    gap: 2.5vw;
    font-size: 5vh;

    ${mobileCss(`
        gap: 5vw;
        font-size: 8vw;
    `)}
`)

const floatingEmoji = (delay: string) => css`
    animation: ${float} 3s ease-in-out ${delay} infinite;
    display: inline-block;
`

const MessageStyle = withMyTheme((theme) => css`
    font-family: ${theme.typography.h1.fontFamily};
    font-size: 5vh;
    text-align: center;
    background: linear-gradient(
        90deg,
        ${theme.palette.primary.main},
        ${theme.palette.primary.dark},
        ${theme.palette.primary.main}
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 4s linear infinite;

    ${mobileCss(`
        font-size: clamp(1.8rem, 7vw, 2.8rem);
    `)}
`)

const SubtextStyle = withMyTheme((theme) => css`
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 2.5vh;
    color: ${theme.palette.text.primary};
    text-align: center;
    max-width: 500px;
    line-height: 1.6;
    opacity: 0.75;

    ${mobileCss(`
        font-size: 1rem;
        max-width: 80vw;
    `)}
`)

const DotsStyle = withMyTheme((theme) => css`
    display: flex;
    gap: 1rem;
`)

const DotStyle = (delay: string) => withMyTheme((theme) => css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${theme.palette.primary.main};
    animation: ${pulse} 2s ease-in-out ${delay} infinite;
`)

interface StayTunedPlaceholderProps {
    message: string
    subtext: string
    emojis: string[]
}

export const StayTunedPlaceholder = ({ message, subtext, emojis }: StayTunedPlaceholderProps) => {
    return (
        <div css={ContainerStyle}>
            <div css={EmojiRowStyle}>
                {emojis.map((emoji, i) => (
                    <span key={i} css={floatingEmoji(`${i * 0.4}s`)}>{emoji}</span>
                ))}
            </div>

            <div css={MessageStyle}>{message}</div>

            <div css={SubtextStyle}>{subtext}</div>

            <div css={DotsStyle}>
                <div css={DotStyle('0s')} />
                <div css={DotStyle('0.4s')} />
                <div css={DotStyle('0.8s')} />
            </div>
        </div>
    )
}
